/**
 * Local authentication and user profile — all data stored locally in IndexedDB.
 * No server, no email, no passwords sent anywhere.
 * 
 * Uses a simple PIN/passphrase stored as a salted SHA-256 hash in IndexedDB.
 * The "account" is purely local — survives browser restarts, works offline.
 */

import { openDB, type IDBPDatabase } from "idb";

const DB_NAME = "astrogenesis_user";
const DB_VERSION = 1;

export interface UserProfile {
  id: string;
  displayName: string;
  /** SHA-256 hash of passphrase + salt */
  passphraseHash: string;
  salt: string;
  createdAt: string;
  lastLoginAt: string;
  /** Billing tier: free | pro | scholar */
  tier: "free" | "pro" | "scholar";
  /** User's own birth chart — set during onboarding */
  ownChart?: OwnChartData;
  /** Email address (stored locally, never sent) */
  email?: string;
}

export interface OwnChartData {
  year: number;
  month: number;
  date: number;
  hours: number;
  minutes: number;
  seconds: number;
  latitude: number;
  longitude: number;
  timezone: number;
  observation_point: "topocentric" | "geocentric";
  ayanamsha: "lahiri" | "raman" | "kp";
  placeName?: string;
}

// ── IndexedDB helpers ──────────────────────────────────────────────────────

async function getDb(): Promise<IDBPDatabase> {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains("profiles")) {
        db.createObjectStore("profiles", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("sessions")) {
        db.createObjectStore("sessions", { keyPath: "id" });
      }
    },
  });
}

// ── Crypto helpers (pure JS SHA-256 via SubtleCrypto) ──────────────────────

async function sha256(message: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function generateSalt(): string {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return Array.from(arr).map((b) => b.toString(36)).join("");
}

function generateId(): string {
  return crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ── Auth API ───────────────────────────────────────────────────────────────

export async function createAccount(displayName: string, passphrase: string): Promise<UserProfile> {
  const db = await getDb();
  const existing = await db.getAll("profiles");
  if (existing.length > 0) {
    throw new Error("Account already exists. Delete it first or log in.");
  }

  const salt = generateSalt();
  const passphraseHash = await sha256(passphrase + salt);
  const now = new Date().toISOString();

  const profile: UserProfile = {
    id: "local-user",
    displayName,
    passphraseHash,
    salt,
    createdAt: now,
    lastLoginAt: now,
    tier: "free",
    ownChart: undefined,
    email: undefined,
  };

  await db.put("profiles", profile);
  await createSession(profile.id);

  // Cache in localStorage for quick reads
  localStorage.setItem("user_session", JSON.stringify({ userId: profile.id, loggedInAt: now }));
  localStorage.setItem("user_profile_cache", JSON.stringify(profile));

  return profile;
}

export async function login(passphrase: string): Promise<UserProfile> {
  const db = await getDb();
  const profile = await db.get("profiles", "local-user");
  if (!profile) {
    throw new Error("No account found. Create one first.");
  }

  const hash = await sha256(passphrase + profile.salt);
  if (hash !== profile.passphraseHash) {
    throw new Error("Incorrect passphrase.");
  }

  profile.lastLoginAt = new Date().toISOString();
  await db.put("profiles", profile);
  await createSession(profile.id);

  localStorage.setItem("user_session", JSON.stringify({ userId: profile.id, loggedInAt: profile.lastLoginAt }));
  const { passphraseHash: _, salt: _s, ...cacheSafe } = profile;
  localStorage.setItem("user_profile_cache", JSON.stringify(profile));

  return profile;
}

export async function logout(): Promise<void> {
  localStorage.removeItem("user_session");
  localStorage.removeItem("user_profile_cache");
  const db = await getDb();
  const sessions = await db.getAll("sessions");
  for (const s of sessions) {
    await db.delete("sessions", s.id);
  }
}

export async function getSession(): Promise<{ userId: string; loggedInAt: string } | null> {
  const cached = localStorage.getItem("user_session");
  if (cached) return JSON.parse(cached);
  return null;
}

export async function getProfile(): Promise<UserProfile | null> {
  // Try cache first
  const cached = localStorage.getItem("user_profile_cache");
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch {}
  }

  const db = await getDb();
  const profile = await db.get("profiles", "local-user");
  if (profile) {
    localStorage.setItem("user_profile_cache", JSON.stringify(profile));
    return profile;
  }
  return null;
}

export async function updateProfile(patch: Partial<UserProfile>): Promise<UserProfile> {
  const db = await getDb();
  const profile = await db.get("profiles", "local-user");
  if (!profile) throw new Error("No profile to update.");

  const updated = { ...profile, ...patch, lastLoginAt: new Date().toISOString() };
  await db.put("profiles", updated);
  localStorage.setItem("user_profile_cache", JSON.stringify(updated));
  return updated;
}

export async function setOwnChart(chart: OwnChartData): Promise<void> {
  // Guest users (new visitors, incognito) have no profile yet. The chart is
  // still persisted via saveChart() + localStorage, so silently skip the
  // profile update instead of throwing — otherwise the Onboarding catch
  // block dumps the user back to step 0 with "No profile to update."
  const db = await getDb();
  const profile = await db.get("profiles", "local-user");
  if (!profile) return;
  await updateProfile({ ownChart: chart });
}

export async function changePassphrase(oldPass: string, newPass: string): Promise<void> {
  const db = await getDb();
  const profile = await db.get("profiles", "local-user");
  if (!profile) throw new Error("No account.");

  const oldHash = await sha256(oldPass + profile.salt);
  if (oldHash !== profile.passphraseHash) throw new Error("Current passphrase is incorrect.");

  const newSalt = generateSalt();
  const newHash = await sha256(newPass + newSalt);
  await db.put("profiles", { ...profile, salt: newSalt, passphraseHash: newHash });
  localStorage.removeItem("user_profile_cache");
}

export async function deleteAccount(passphrase: string): Promise<void> {
  // Verify passphrase first
  await login(passphrase);
  
  const db = await getDb();
  await db.delete("profiles", "local-user");
  // Clear all sessions
  const sessions = await db.getAll("sessions");
  for (const s of sessions) {
    await db.delete("sessions", s.id);
  }
  localStorage.removeItem("user_session");
  localStorage.removeItem("user_profile_cache");
  // Also clear saved charts
  localStorage.removeItem("charts_index");
}

async function createSession(userId: string): Promise<void> {
  const db = await getDb();
  const session = {
    id: `session-${Date.now()}`,
    userId,
    createdAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
  };
  await db.put("sessions", session);
}
